class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
    this.canAddToCart = false;
    // AQ-1096: mirrors the main button's data-requires-size-selection (set in
    // main-product.liquid, cleared by variants.js once any option is picked) —
    // distinguishes "blocked because no size is picked" from other reasons
    // canAddToCart can be false, so the sticky CTA only surfaces the size
    // validation message for the case it actually applies to.
    this.requiresSizeSelection = false;
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    this.addToCartForm = document.querySelector(".wt-product__add-to-cart_form");
    // The buttons row nested inside the form — this is what actually needs
    // to be on-screen before the sticky button hides itself; the outer form
    // wrapper (.wt-product__add-to-cart_form) can be considerably taller
    // (variant pickers, etc.), so tracking that would show/hide too early.
    this.stickyVisibilityTarget =
      document.querySelector(".wt-product__add-to-cart_form--wrapper") ||
      this.addToCartForm;
    this.btn = this.querySelector("button");
    this.canAddToCart = this.dataset.addToCart === "";

    if (!this.btn) {
      // No button rendered inside <sticky-buy-button> at all — nothing to wire up.
      console.warn(
        "sticky-buy-button: no <button> found inside the component — sticky CTA disabled."
      );
      return;
    }

    this.btnText = this.btn.querySelector("span");
    this.loader = this.btn.querySelector(".loading-overlay__spinner");

    if (!this.addToCartForm) {
      // The main product form has moved/been renamed and this component can no
      // longer find it to scroll to or sync state with — surface this loudly
      // (e.g. in Shopify's theme-editor JS console) instead of the sticky CTA
      // just silently doing nothing on click.
      console.warn(
        "sticky-buy-button: .wt-product__add-to-cart_form not found — sticky CTA disabled."
      );
      return;
    }

    this.classList.remove(this.activeClass);
    document.body.classList.remove("sticky-buy-visible");

    this.btn.addEventListener("click", (e) => {
      if (
        this.btn.hasAttribute("disabled") ||
        this.btn.getAttribute("aria-disabled") === "true"
      ) {
        // Sold out, or an add-to-cart request is already in flight — do nothing.
        e.preventDefault();
        return;
      }

      if (this.canAddToCart) {
        // Submit the main product form
        e.preventDefault();
        const mainForm = this.addToCartForm.querySelector("form");
        if (mainForm) {
          const submitBtn = mainForm.querySelector('[name="add"]');
          if (submitBtn) submitBtn.click();
        }
      } else {
        e.preventDefault();
        if (this.requiresSizeSelection) {
          // AQ-1096: same "Please select a size" validation message AQ-1078 added
          // to the main add-to-cart button — the sticky CTA scrolled to the form
          // silently before, with no explanation.
          this.addToCartForm
            .querySelector("product-form")
            ?.toggleSizeSelectionError(true);
        }
        this.addToCartForm.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    });

    // Listen for variant changes from the main add-to-cart button
    this.observeMainButton();

    const observer = new IntersectionObserver(
      ([entry]) => {
        const hasScrolledPast =
          !entry.isIntersecting && Number(entry.boundingClientRect.top) < 121;
        this.classList.toggle(this.activeClass, hasScrolledPast);
        // #launcher (Zendesk chat widget) is injected as a direct child of
        // <body>, outside this component's DOM branch, so its own CSS can't
        // reach `this.activeClass` with a sibling selector — mirror the
        // state onto <body> instead.
        document.body.classList.toggle("sticky-buy-visible", hasScrolledPast);
      },
      { threshold: 0 }
    );
    observer.observe(this.stickyVisibilityTarget);
  }

  observeMainButton() {
    // Find the main submit button and mirror its state
    const mainSubmitBtn = this.addToCartForm.querySelector('[name="add"]');
    if (!mainSubmitBtn) {
      console.warn(
        "sticky-buy-button: main [name=\"add\"] submit button not found — sticky CTA won't mirror its state."
      );
      return;
    }

    // Sync initial state
    this.syncWithMainButton(mainSubmitBtn);

    // Watch for attribute/text changes on the main button
    const observer = new MutationObserver(() => {
      this.syncWithMainButton(mainSubmitBtn);
    });

    observer.observe(mainSubmitBtn, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  }

  syncWithMainButton(mainBtn) {
    const mainBtnText = mainBtn.querySelector("span");
    if (!mainBtnText || !this.btnText) {
      console.warn(
        "sticky-buy-button: expected <span> text node missing on the main or sticky button — sticky CTA state can't be synced."
      );
      return;
    }

    const text = mainBtnText.textContent.trim();
    const isSoldOut = text === window.variantStrings?.soldOut;
    const isLoading = mainBtn.classList.contains("loading");

    this.btnText.textContent = text;
    this.btn.classList.toggle("loading", isLoading);
    if (this.loader) this.loader.classList.toggle("hidden", !isLoading);

    if (isSoldOut) {
      // Sold out: there's no variant to scroll to and nothing to add —
      // disable the sticky CTA for real (native disabled: no hover, no click).
      this.btn.setAttribute("disabled", "");
      this.canAddToCart = false;
      return;
    }

    this.btn.removeAttribute("disabled");

    if (isLoading) {
      // The add-to-cart request (triggered from either button) is in flight —
      // mirror the main button's disabled-while-loading state.
      this.btn.setAttribute("aria-disabled", "true");
      return;
    }

    this.btn.removeAttribute("aria-disabled");

    // AQ-1096: same signal product-form.js reads on the main button.
    this.requiresSizeSelection = mainBtn.dataset.requiresSizeSelection === "true";

    const isDisabled =
      mainBtn.hasAttribute("disabled") ||
      mainBtn.getAttribute("aria-disabled") === "true";
    if (isDisabled) {
      // Main button is disabled (no variant selected yet)
      // Sticky button stays clickable — scrolls to form
      this.canAddToCart = false;
    } else {
      // Variant selected and available — sticky button adds to cart
      this.canAddToCart = true;
    }
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 769px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 44)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 44)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
